import React, { useMemo } from "react";
import Dropdown from "../../common/dropDown";
import {
  LOGIC_ACTIONS,
  LOGIC_OPERATORS,
  createEmptyLogicItem,
  isLayoutField,
} from "./esourceFieldTypes";

/**
 * Form-level conditional logic editor (WHEN / THEN rows).
 */
export default function ESourceConditionalLogicBuilder({
  logic = [],
  fields = [],
  onChange,
  focusFieldId = null,
  compact = false,
  hideToolbar = false,
}) {
  const fieldOptions = useMemo(
    () =>
      (fields || [])
        .filter((f) => !isLayoutField(f.type) && f.type !== "pagebreak")
        .map((f) => ({
          value: f.id,
          label: f.label || f.fieldName || f.id,
        })),
    [fields],
  );

  const matchOptions = [
    { value: "all", label: "All conditions (AND)" },
    { value: "any", label: "Any condition (OR)" },
  ];

  const updateItem = (id, patch) => {
    onChange?.(
      (logic || []).map((item) =>
        item.id === id ? { ...item, ...patch } : item,
      ),
    );
  };

  const removeItem = (id) => {
    onChange?.((logic || []).filter((item) => item.id !== id));
  };

  const addItem = () => {
    const item = createEmptyLogicItem();
    if (focusFieldId) {
      item.actions = [{ action: "show", targetId: focusFieldId }];
    }
    onChange?.([...(logic || []), item]);
  };

  return (
    <div
      className={`esource-logic-builder${
        compact ? " esource-logic-builder--compact" : ""
      }`}
    >
      {!hideToolbar ? (
        <div
          className={`esource-logic-builder__toolbar d-flex ${
            compact
              ? "justify-content-end align-items-center"
              : "justify-content-between align-items-center mb-2"
          }`}
        >
          {!compact ? (
            <p className="mb-0 small text-muted">
              When conditions match, apply show/hide or enable/disable actions.
            </p>
          ) : null}
          <button
            type="button"
            className="btn btn-sm btn-outline-primary"
            onClick={addItem}
          >
            Add rule
          </button>
        </div>
      ) : null}

      {(logic || []).length === 0 ? (
        <p className="text-muted small mb-0">
          {compact
            ? "No rules yet. Add a rule to show, hide, enable, or disable this field."
            : "No conditional logic yet."}
        </p>
      ) : null}

      {(logic || []).map((item, index) => {
        const matchSel =
          matchOptions.find((o) => o.value === item.match) || matchOptions[0];
        return (
          <div key={item.id} className="esource-logic-builder__card">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <strong className="small">Rule {index + 1}</strong>
              <button
                type="button"
                className="btn btn-sm btn-outline-danger"
                onClick={() => removeItem(item.id)}
              >
                Remove
              </button>
            </div>

            <div className="esource-builder-field">
              <label className="esource-builder-field__label">Match</label>
              <Dropdown
                options={matchOptions}
                isMulti={false}
                showSearch={false}
                selected={matchSel}
                onSelect={(opt) =>
                  updateItem(item.id, { match: opt?.value || "all" })
                }
              />
            </div>

            <div className="small fw-semibold mb-1">When</div>
            {(item.rules || []).map((rule, ri) => {
              const fieldSel =
                fieldOptions.find((o) => o.value === rule.fieldId) || null;
              const opSel =
                LOGIC_OPERATORS.find((o) => o.value === rule.operator) ||
                LOGIC_OPERATORS[0];
              const needsValue = !["isEmpty", "isNotEmpty"].includes(
                rule.operator,
              );
              return (
                <div key={`r-${ri}`} className="esource-logic-builder__row">
                  <Dropdown
                    options={fieldOptions}
                    isMulti={false}
                    showSearch
                    selected={fieldSel}
                    onSelect={(opt) => {
                      const rules = [...(item.rules || [])];
                      rules[ri] = {
                        ...rules[ri],
                        fieldId: opt?.value || "",
                      };
                      updateItem(item.id, { rules });
                    }}
                  />
                  <Dropdown
                    options={LOGIC_OPERATORS}
                    isMulti={false}
                    showSearch={false}
                    selected={opSel}
                    onSelect={(opt) => {
                      const rules = [...(item.rules || [])];
                      rules[ri] = {
                        ...rules[ri],
                        operator: opt?.value || "equals",
                      };
                      updateItem(item.id, { rules });
                    }}
                  />
                  {needsValue ? (
                    <input
                      className="form-control form-control-sm"
                      value={rule.value || ""}
                      placeholder="Value"
                      onChange={(e) => {
                        const rules = [...(item.rules || [])];
                        rules[ri] = {
                          ...rules[ri],
                          value: e.target.value,
                        };
                        updateItem(item.id, { rules });
                      }}
                    />
                  ) : (
                    <span className="form-text">—</span>
                  )}
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-danger"
                    title="Remove condition"
                    onClick={() => {
                      const rules = (item.rules || []).filter(
                        (_, i) => i !== ri,
                      );
                      updateItem(item.id, {
                        rules: rules.length
                          ? rules
                          : [
                              {
                                fieldId: "",
                                operator: "equals",
                                value: "",
                              },
                            ],
                      });
                    }}
                  >
                    <i className="fas fa-trash-alt" aria-hidden="true" />
                  </button>
                </div>
              );
            })}
            <button
              type="button"
              className="btn btn-sm btn-link px-0 mb-2"
              onClick={() =>
                updateItem(item.id, {
                  rules: [
                    ...(item.rules || []),
                    { fieldId: "", operator: "equals", value: "" },
                  ],
                })
              }
            >
              + Add condition
            </button>

            <div className="small fw-semibold mb-1">Then</div>
            {(item.actions || []).map((action, ai) => {
              const actSel =
                LOGIC_ACTIONS.find((o) => o.value === action.action) ||
                LOGIC_ACTIONS[0];
              const targetSel =
                fieldOptions.find((o) => o.value === action.targetId) || null;
              return (
                <div
                  key={`a-${ai}`}
                  className="esource-logic-builder__row esource-logic-builder__row--action"
                >
                  <Dropdown
                    options={LOGIC_ACTIONS}
                    isMulti={false}
                    showSearch={false}
                    selected={actSel}
                    onSelect={(opt) => {
                      const actions = [...(item.actions || [])];
                      actions[ai] = {
                        ...actions[ai],
                        action: opt?.value || "show",
                      };
                      updateItem(item.id, { actions });
                    }}
                  />
                  <Dropdown
                    options={fieldOptions}
                    isMulti={false}
                    showSearch
                    selected={targetSel}
                    onSelect={(opt) => {
                      const actions = [...(item.actions || [])];
                      actions[ai] = {
                        ...actions[ai],
                        targetId: opt?.value || "",
                      };
                      updateItem(item.id, { actions });
                    }}
                  />
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-danger"
                    title="Remove action"
                    onClick={() => {
                      const actions = (item.actions || []).filter(
                        (_, i) => i !== ai,
                      );
                      updateItem(item.id, {
                        actions: actions.length
                          ? actions
                          : [{ action: "show", targetId: "" }],
                      });
                    }}
                  >
                    <i className="fas fa-trash-alt" aria-hidden="true" />
                  </button>
                </div>
              );
            })}
            <button
              type="button"
              className="btn btn-sm btn-link px-0"
              onClick={() =>
                updateItem(item.id, {
                  actions: [
                    ...(item.actions || []),
                    { action: "show", targetId: "" },
                  ],
                })
              }
            >
              + Add action
            </button>
          </div>
        );
      })}
    </div>
  );
}
